import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { FilterFieldDef } from '../components/molecules/FilterBar/AddFilterModal.types';

dayjs.extend(customParseFormat);

// Shared formats for date filters. Date-only fields use DATE_*; fields that opt
// in via `withTime` use DATETIME_*. The value (wire) format MUST contain no
// letters: api-services (mapBindParameters) treats any date-column filter value
// containing [a-zA-Z] as a relative-date keyword and runs it through
// getRelativeDate, which returns null for a real timestamp — so an ISO `T`
// separator makes the filter bind `>= NULL` and return zero rows. A space works:
// Postgres coerces `YYYY-MM-DD HH:mm` to a timestamp, the operator comma-split
// keeps the value intact, and the browser percent-encodes the space in transit.
export const DATE_DISPLAY_FORMAT = 'DD/MM/YYYY';
export const DATE_VALUE_FORMAT = 'YYYY-MM-DD';
export const DATETIME_DISPLAY_FORMAT = 'DD/MM/YYYY HH:mm';
export const DATETIME_VALUE_FORMAT = 'YYYY-MM-DD HH:mm';

export const dateFilterValueFormat = (withTime?: boolean): string =>
  withTime ? DATETIME_VALUE_FORMAT : DATE_VALUE_FORMAT;

export const dateFilterDisplayFormat = (withTime?: boolean): string =>
  withTime ? DATETIME_DISPLAY_FORMAT : DATE_DISPLAY_FORMAT;

// Normalises a legacy datetime filter value to the current wire form. PR #420
// briefly emitted with-time values as ISO `YYYY-MM-DDTHH:mm`; convert that `T`
// to a space so a saved report / URL from that window still hydrates and
// re-emits in the current space-separated format. Space and date-only values
// pass through unchanged.
export const normalizeDateFilterValue = (raw: string): string =>
  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(raw) ? raw.replace('T', ' ') : raw;

// Formats a stored date-filter value for display, falling back to the raw value
// when it doesn't parse in the expected format.
export const formatDateFilterForDisplay = (
  val: string,
  valueFormat: string,
  displayFormat: string
): string => {
  if (!val) return '';
  const parsed = dayjs(normalizeDateFilterValue(val), valueFormat, true);
  return parsed.isValid() ? parsed.format(displayFormat) : val;
};

// Chip label value for a filter: date fields render as DD/MM/YYYY (plus HH:mm
// for `withTime` fields); everything else passes through unchanged.
export const chipDisplayValue = (def: FilterFieldDef | undefined, raw: string): string =>
  def?.type === 'date'
    ? formatDateFilterForDisplay(
        raw,
        dateFilterValueFormat(def.withTime),
        dateFilterDisplayFormat(def.withTime)
      )
    : raw;
